import {
  AdditiveBlending,
  BackSide,
  Color,
  DoubleSide,
  ShaderMaterial
} from 'three';
import { sharedUniforms } from '../core/FrameUniforms.js';
import { noiseGLSL } from '../shaders/lib/noise.glsl.js';

const VERTEX = /* glsl */ `
  uniform float uTime;
  uniform float uCharge;
  uniform float uSeed;
  uniform float uShell;
  varying vec3 vNormalW;
  varying vec3 vViewW;
  varying vec3 vWorld;
  varying float vNoise;

  ${noiseGLSL}

  void main() {
    float speed = mix(1.4, 5.5, uCharge);
    float coarse = snoise(normal * mix(1.7, 3.8, uCharge) + vec3(uSeed, uTime * speed, -uSeed));
    float fine = snoise(normal * 7.0 - vec3(uTime * speed * 1.8, uSeed, uTime));
    float displacement = (coarse * 0.12 + fine * 0.035) * mix(0.35, 1.0, uCharge) * (1.0 + uShell);
    vec3 displaced = position + normal * displacement;
    vec4 world = modelMatrix * vec4(displaced, 1.0);
    vNormalW = normalize(mat3(modelMatrix) * normal);
    vViewW = normalize(cameraPosition - world.xyz);
    vWorld = world.xyz;
    vNoise = coarse * 0.5 + 0.5;
    gl_Position = projectionMatrix * viewMatrix * world;
  }
`;

const FRAGMENT = /* glsl */ `
  uniform float uTime;
  uniform float uCharge;
  uniform float uFade;
  uniform float uShell;
  uniform float uIntensity;
  uniform float uGlobalGlow;
  uniform vec3 uRed;
  uniform vec3 uOrange;
  uniform vec3 uYellow;
  uniform vec3 uBlue;
  uniform vec3 uPurple;
  uniform vec3 uWhite;
  varying vec3 vNormalW;
  varying vec3 vViewW;
  varying vec3 vWorld;
  varying float vNoise;

  ${noiseGLSL}

  void main() {
    float fresnel = pow(1.0 - abs(dot(normalize(vNormalW), normalize(vViewW))), mix(2.8, 1.15, uShell));
    float rivers = 0.5 + 0.5 * snoise(vWorld * mix(2.3, 4.8, uCharge) + vec3(0.0, -uTime * 4.2, uTime * 1.7));
    float veins = pow(0.5 + 0.5 * sin(rivers * 18.0 + vNoise * 9.0 - uTime * 10.0), 5.0);

    vec3 thermal = mix(uRed, uOrange, smoothstep(0.05, 0.5, rivers));
    thermal = mix(thermal, uYellow, smoothstep(0.45, 0.8, rivers));
    thermal = mix(thermal, uWhite, veins * (0.45 + uCharge * 0.55));
    vec3 arcane = mix(uPurple, uBlue, smoothstep(0.15, 0.85, vNoise));
    vec3 color = mix(thermal, arcane, uShell * (0.45 + 0.25 * sin(vNoise * 12.0 + uTime * 5.0)));
    color = mix(color, uWhite, fresnel * (0.55 + uCharge * 0.45));

    float pulse = 0.84 + 0.16 * sin(uTime * mix(7.0, 19.0, uCharge) + vNoise * 7.0);
    float alpha = mix(0.94, fresnel * 0.72 + veins * 0.12, uShell) * uFade;
    if (alpha < 0.004) discard;
    color *= uIntensity * uGlobalGlow * pulse * mix(1.0, 1.75, uCharge);
    gl_FragColor = vec4(color, alpha);
  }
`;

function uniforms(shell) {
  return sharedUniforms({
    uCharge: { value: 0 },
    uFade: { value: 1 },
    uShell: { value: shell },
    uSeed: { value: Math.random() * 100 },
    uIntensity: { value: 1 },
    uRed: { value: new Color('#ff2417') },
    uOrange: { value: new Color('#ff7818') },
    uYellow: { value: new Color('#ffd84a') },
    uBlue: { value: new Color('#39bfff') },
    uPurple: { value: new Color('#a743ff') },
    uWhite: { value: new Color('#fffdf2') }
  });
}

export function createCataclysmMaterial(shell = 0) {
  return new ShaderMaterial({
    transparent: true,
    depthWrite: false,
    depthTest: true,
    blending: AdditiveBlending,
    side: shell > 0 ? BackSide : DoubleSide,
    toneMapped: false,
    uniforms: uniforms(shell),
    vertexShader: VERTEX,
    fragmentShader: FRAGMENT
  });
}

export function syncCataclysmMaterial(material, config, charge, fade = 1) {
  const u = material.uniforms;
  u.uCharge.value = charge;
  u.uFade.value = fade;
  u.uIntensity.value = config.shaderIntensity ?? 1;
  u.uRed.value.set(config.colorRed);
  u.uOrange.value.set(config.colorOrange);
  u.uYellow.value.set(config.colorYellow);
  u.uBlue.value.set(config.colorBlue);
  u.uPurple.value.set(config.colorPurple);
  u.uWhite.value.set(config.colorWhite);
}
